import React, { FC, useMemo } from 'react'

import { LinkProps } from '../A'
import { IconName } from '../Icon'
import AccessibleHiddenText from '../AccessibleHiddenText'

import * as SC from './styled'

export type ActionButtonProps = {
  className?: string
  icon?: IconName
  iconPosition?: 'left' | 'right'
  text?: string
  variantColor?: string
  variantIcon?: 'only' | 'composite'
  exponent?: string
  progress?: number
  variantColorScheme?:
    | 'white'
    | 'whiteHoverBlack'
    | 'black'
    | 'colored'
    | 'iconBlack'
    | 'iconWhite'
    | 'iconFullWhite'
    | 'iconRedMojo'
    | 'iconGreenApple'
  isActive?: boolean
  isShrinkable?: boolean
  disabled?: boolean
} & (
  | {
      variantType: 'link'
      actionProps: LinkProps
    }
  | {
      variantType: 'button'
      actionProps: React.ComponentPropsWithoutRef<'button'>
    }
  | {
      variantType: 'fake'
      actionProps: React.ComponentPropsWithoutRef<'button'>
    }
)

const ActionButton: FC<ActionButtonProps> = (props) => {
  const {
    className,
    icon = null,
    iconPosition = 'left',
    text,
    exponent,
    variantColor,
    variantIcon,
    variantColorScheme,
    isActive,
    variantType,
    actionProps,
    isShrinkable = false,
    progress,
    disabled,
  } = props

  const renderContent = useMemo(() => {
    return (
      <>
        {icon && iconPosition === 'left' && (
          <SC.ActionIcon icon={icon} className="button-icon" />
        )}
        {variantIcon === 'only' ? (
          <AccessibleHiddenText text={text} />
        ) : (
          <SC.ActionText $isShrinkable={isShrinkable} variantIcon={variantIcon}>
            {text}
          </SC.ActionText>
        )}
        {icon && iconPosition === 'right' && (
          <SC.ActionIcon icon={icon} className="button-icon" />
        )}
      </>
    )
  }, [icon, iconPosition, variantIcon, text, isShrinkable])

  return (
    <SC.Container className={className}>
      {exponent && <SC.Exponent className="exponent">{exponent}</SC.Exponent>}
      {variantType === 'link' ? (
        <SC.Link
          variantIcon={variantIcon}
          variantColor={variantColor}
          variantColorScheme={variantColorScheme}
          isActive={isActive}
          hasIcon={icon ? true : false}
          className={disabled ? 'disabled' : ''}
          {...actionProps}
        >
          <SC.Progress $value={progress} />
          {renderContent}
        </SC.Link>
      ) : variantType === 'button' ? (
        <SC.Button
          variantIcon={variantIcon}
          variantColor={variantColor}
          variantColorScheme={variantColorScheme}
          isActive={isActive}
          hasIcon={icon ? true : false}
          className={disabled ? 'disabled' : ''}
          {...actionProps}
        >
          <SC.Progress $value={progress} />
          {renderContent}
        </SC.Button>
      ) : variantType === 'fake' ? (
        <SC.Fake
          variantIcon={variantIcon}
          variantColor={variantColor}
          variantColorScheme={variantColorScheme}
          isActive={isActive}
          hasIcon={icon ? true : false}
          className={disabled ? 'disabled' : ''}
        >
          <SC.Progress $value={progress} />
          {renderContent}
        </SC.Fake>
      ) : null}
    </SC.Container>
  )
}

export default ActionButton
